/**
 * Pure rules behind the invoice detail page (design D): which actions a
 * document offers, the balance card, the payments list and the activity
 * timeline copy. Kept free of React so every status can be unit-tested; the
 * server still decides — these only choose what to show.
 */
import type { DocumentDetail, InvoiceDoc, InvoiceEvent, Line, Payment } from '../../internals/repository/InvoiceRepository';
import { METHOD_LABEL, money } from './format';
import { divRound, qtyThousandths } from './math/invoiceMath';
import { rejectLabel } from './approvalsModel';

export type DetailAction =
  | 'edit' | 'issue' | 'deleteDraft'
  | 'recordPayment' | 'creditNote' | 'void'
  | 'duplicate' | 'pdf' | 'regenerateLink' | 'copyLink' | 'reminders';

type DocLike = Pick<InvoiceDoc, 'docType' | 'status'> & Partial<InvoiceDoc>;

/** An issued invoice that still has a balance to collect. */
export const isOpenInvoice = (d: DocLike) =>
  d.docType === 'INVOICE' && (d.status === 'ISSUED' || d.status === 'PARTLY_PAID') && (d.balanceMinor ?? 1) > 0;

/** Money already against the invoice; any of it locks a non-tax revision (D-INV-5). */
export const hasMoney = (d: Partial<InvoiceDoc>) =>
  (d.paidMinor || 0) + (d.creditedMinor || 0) + (d.depositAppliedMinor || 0) + (d.pendingClaimMinor || 0) > 0;

export function availableActions(d: DocLike): Set<DetailAction> {
  const a = new Set<DetailAction>();
  if (d.status === 'DRAFT') {
    a.add('edit'); a.add('issue'); a.add('deleteDraft');
    return a;
  }
  if (d.shareUrl) a.add('copyLink');
  if (d.docType === 'QUOTATION') {
    a.add('duplicate'); a.add('pdf');
    return a;
  }
  if (d.docType !== 'INVOICE') {
    a.add('pdf');
    return a;
  }
  a.add('duplicate'); a.add('pdf');
  if (isOpenInvoice(d)) {
    a.add('recordPayment'); a.add('creditNote'); a.add('void'); a.add('regenerateLink');
    if (d.reminders) a.add('reminders');
    if (!d.isTaxInvoice && d.status === 'ISSUED' && !hasMoney(d)) a.add('edit');
  }
  return a;
}

/** Share of the total collected (paid, credited or deposit), 0–100, whole percent. */
export function paidPercent(d: Pick<InvoiceDoc, 'totalMinor' | 'balanceMinor' | 'status'>): number {
  if (d.status === 'VOID' || !(d.totalMinor > 0)) return 0;
  const done = d.totalMinor - Math.max(0, d.balanceMinor);
  return Math.max(0, Math.min(100, Math.round((done * 100) / d.totalMinor)));
}

export interface BalanceRow { label: string; value: string; tone?: 'pos' | 'strong' }

export function balanceRows(d: InvoiceDoc): BalanceRow[] {
  const cur = d.currency;
  const rows: BalanceRow[] = [{ label: 'Total', value: money(d.totalMinor, cur) }];
  if (d.depositAppliedMinor) rows.push({ label: 'Deposit', value: `− ${money(d.depositAppliedMinor, cur)}` });
  if (d.paidMinor) rows.push({ label: 'Paid', value: `− ${money(d.paidMinor, cur)}`, tone: 'pos' });
  if (d.creditedMinor) rows.push({ label: 'Credit notes', value: `− ${money(d.creditedMinor, cur)}` });
  rows.push({ label: 'Balance due', value: d.status === 'VOID' ? '—' : money(Math.max(0, d.balanceMinor), cur), tone: 'strong' });
  return rows;
}

export interface MoneyRow {
  key: string;
  kind: 'payment' | 'credit';
  title: string;
  /** YYYY-MM-DD for payments; epoch ms for credit notes (from the event). */
  paidOn?: string;
  at?: number;
  reference: string;
  amountMinor: number;
  number?: string | null;
  docId?: string;
  refunded?: boolean;
  /** Set on a rejected customer claim: why it was rejected. Not money; shown muted. */
  rejected?: string;
}

/**
 * Payments (with their receipt numbers) then credit notes. Receipt and credit
 * note numbers come from `related.children`; a credit note's date and reason
 * from its CREDIT_NOTE_ISSUED event. Claims waiting for approval are not money
 * yet and are left out (they get their own card); rejected claims follow the
 * payments, muted, so the owner can see what the customer was told.
 */
export function moneyRows(detail: Pick<DocumentDetail, 'payments' | 'events' | 'related'>): MoneyRow[] {
  const children = detail.related?.children ?? [];
  const byId = new Map(children.map((c) => [c.docId, c]));
  const pays: MoneyRow[] = (detail.payments || [])
    .filter((p: Payment) => p.status === 'SUCCEEDED' || p.status === 'REFUNDED')
    .map((p) => {
      const receipt = p.receiptDocId ? byId.get(p.receiptDocId) : undefined;
      const ref = p.method === 'CHEQUE'
        ? [p.chequeNo && `Cheque ${p.chequeNo}`, p.chequeBank, p.reference].filter(Boolean).join(' · ')
        : p.reference || '';
      return {
        key: p.paymentId, kind: 'payment', title: METHOD_LABEL[p.method] ?? p.method, paidOn: p.paidOn,
        reference: ref || 'No reference', amountMinor: p.approvedMinor ?? p.amountMinor,
        number: receipt?.docType === 'RECEIPT' ? receipt.number : null, docId: receipt?.docId,
        refunded: p.status === 'REFUNDED',
      } as MoneyRow;
    });
  const rejected: MoneyRow[] = (detail.payments || [])
    .filter((p: Payment) => p.status === 'REJECTED')
    .map((p) => ({
      key: p.paymentId, kind: 'payment', title: METHOD_LABEL[p.method] ?? p.method, paidOn: p.paidOn,
      reference: p.reference || 'No reference', amountMinor: p.amountMinor,
      rejected: rejectLabel(p.rejectCode, p.rejectReason),
    }));
  const cnEvents = new Map<string, InvoiceEvent>();
  (detail.events || []).forEach((e) => {
    const id = (e.meta as any)?.creditNoteId;
    if (e.type === 'CREDIT_NOTE_ISSUED' && id) cnEvents.set(id, e);
  });
  const credits: MoneyRow[] = children.filter((c) => c.docType === 'CREDIT_NOTE').map((c) => {
    const e = cnEvents.get(c.docId);
    return {
      key: c.docId, kind: 'credit', title: `Credit note ${c.number ?? ''}`.trim(), at: e?.createdAt,
      reference: ((e?.meta as any)?.reason as string) || '', amountMinor: c.totalMinor, number: c.number, docId: c.docId,
    };
  });
  return [...pays, ...rejected, ...credits];
}

/** Credit-note preview for `q` of a line: lineTotal × q / quantity, half up. The server's figure is final. */
export function creditLineValue(line: Pick<Line, 'lineTotalMinor' | 'quantity'>, q: number): number {
  const of = qtyThousandths(line.quantity);
  const part = qtyThousandths(q);
  if (!(of > 0) || !(part > 0)) return 0;
  return divRound((line.lineTotalMinor || 0) * Math.min(part, of), of);
}

/** A credit quantity is valid when it is 0..line quantity with at most 3 decimals. */
export function validCreditQty(text: string, max: number): boolean {
  if (text.trim() === '') return true;
  if (!/^\d*(\.\d{0,3})?$/.test(text.trim())) return false;
  const q = Number(text);
  return q >= 0 && qtyThousandths(q) <= qtyThousandths(max);
}

// ── Activity timeline ──────────────────────────────────────────────────────

export type GlyphTone = 'neutral' | 'brand' | 'pos' | 'warn' | 'neg' | 'info';
export interface EventView { glyph: string; tone: GlyphTone; text: string }

/** Shares made from the owner's own device or clipboard (SHARED_MANUAL). */
const MANUAL_SHARE: Record<string, string> = {
  MANUAL_WHATSAPP: 'Shared on WhatsApp from your device',
  MANUAL_SMS: 'Shared by SMS from your device',
  MANUAL_EMAIL: 'Shared by email from your device',
  COPY_LINK: 'Link copied to share',
  COPY_MESSAGE: 'Message copied to share',
  QR_DOWNLOAD: 'QR code downloaded',
};

const CHANNEL: Record<string, string> = {
  EMAIL: 'email', SMS: 'SMS', WHATSAPP: 'WhatsApp',
  MANUAL_WHATSAPP: 'WhatsApp', MANUAL_SMS: 'SMS', MANUAL_EMAIL: 'email', COPY_LINK: 'copied link',
  COPY_MESSAGE: 'copied message', QR_DOWNLOAD: 'QR code',
};

export function actorOf(e: Pick<InvoiceEvent, 'actor' | 'actorName'>): string {
  if (e.actorName) return e.actorName;
  if (e.actor === 'CUSTOMER') return 'the customer';
  if (e.actor === 'SYSTEM') return 'Netlise';
  return 'a team member';
}

export function eventView(e: InvoiceEvent, currency = 'LKR'): EventView {
  const m: any = e.meta || {};
  const after: any = e.after || {};
  const by = actorOf(e);
  const amt = (v: unknown) => money(Number(v) || 0, currency);
  const ch = CHANNEL[e.channel || ''] || (e.channel ? String(e.channel).toLowerCase() : '');
  const join = (...parts: (string | undefined | null | false)[]) => parts.filter(Boolean).join(' · ');
  switch (e.type) {
    case 'CREATED': return { glyph: '+', tone: 'neutral', text: `Draft created by ${by}` };
    case 'EDITED': return { glyph: '✎', tone: 'neutral', text: `Edited by ${by}` };
    case 'ISSUED': return { glyph: '✓', tone: 'brand', text: join(`Issued by ${by}`, after.number) };
    case 'REVISED': return { glyph: '✎', tone: 'brand', text: join(`Revised to revision ${after.revision ?? ''}`.trim(), m.note) };
    case 'SENT': return { glyph: '↗', tone: 'brand', text: ch ? `Sent by ${ch}` : 'Sent' };
    case 'DELIVERED': return { glyph: '✓', tone: 'info', text: ch ? `Delivered by ${ch}` : 'Delivered' };
    case 'READ': return { glyph: '◉', tone: 'info', text: ch ? `Read on ${ch}` : 'Read' };
    case 'FAILED': return { glyph: '!', tone: 'neg', text: ch ? `Not delivered by ${ch}` : 'Not delivered' };
    case 'SHARED_MANUAL':
      return { glyph: 'L', tone: 'neutral', text: MANUAL_SHARE[e.channel || ''] ?? (ch ? `Shared by ${ch}` : 'Shared') };
    case 'VIEWED': return { glyph: '◉', tone: 'brand', text: 'Viewed by the customer' };
    case 'REMINDER_SCHEDULED': return { glyph: '↻', tone: 'warn', text: 'Reminder scheduled' };
    case 'REMINDER_SENT': return { glyph: '↻', tone: 'warn', text: ch ? `Reminder sent by ${ch}` : 'Reminder sent' };
    case 'REMINDER_PAUSED': return { glyph: '‖', tone: 'neutral', text: `Reminders paused by ${by}` };
    case 'REMINDER_RESUMED': return { glyph: '↻', tone: 'neutral', text: `Reminders resumed by ${by}` };
    case 'CLAIM_SUBMITTED':
      return {
        glyph: '?', tone: 'warn',
        text: join('Customer says they paid', m.amountMinor != null && amt(m.amountMinor), m.method && (METHOD_LABEL[m.method] ?? m.method)),
      };
    case 'CLAIM_APPROVED': {
      const lower = m.claimedMinor != null && m.amountMinor != null && Number(m.claimedMinor) > Number(m.amountMinor);
      return {
        glyph: '✓', tone: 'pos',
        text: join(
          `Payment claim approved by ${by}`,
          lower ? `${amt(m.amountMinor)} (claimed ${amt(m.claimedMinor)})` : m.amountMinor != null && amt(m.amountMinor),
          m.receiptNumber,
        ),
      };
    }
    case 'CLAIM_REJECTED':
      return { glyph: '×', tone: 'neg', text: join(`Payment claim rejected by ${by}`, (m.reasonCode || m.reason) && rejectLabel(m.reasonCode, m.reason)) };
    case 'CLAIM_WITHDRAWN': return { glyph: '×', tone: 'neutral', text: 'Payment claim withdrawn' };
    case 'PAYMENT_RECORDED':
      return {
        glyph: 'Rs', tone: 'pos',
        text: join('Payment recorded', `${METHOD_LABEL[m.method] ?? m.method ?? ''} ${amt(m.amountMinor)}`.trim(), m.receiptNumber),
      };
    case 'PAYMENT_REFUNDED': return { glyph: '↩', tone: 'neg', text: join('Payment refunded', m.amountMinor != null && amt(m.amountMinor)) };
    case 'CREDIT_NOTE_ISSUED': return { glyph: 'CN', tone: 'info', text: join(`Credit note ${m.number ?? ''}`.trim(), amt(m.amountMinor)) };
    case 'QUOTE_ACCEPTED': return { glyph: '✓', tone: 'pos', text: 'Accepted by the customer' };
    case 'QUOTE_DECLINED': return { glyph: '×', tone: 'neg', text: join('Declined by the customer', m.note) };
    case 'CONVERTED': return { glyph: '→', tone: 'brand', text: join('Converted to an invoice', m.number) };
    case 'VOIDED': return { glyph: '⊘', tone: 'neg', text: join(`Voided by ${by}`, m.reason) };
    case 'LINK_REGENERATED': return { glyph: '↺', tone: 'neutral', text: 'Share link regenerated' };
    default: return { glyph: '•', tone: 'neutral', text: String(e.type).toLowerCase().replace(/_/g, ' ') };
  }
}

// ── Errors ─────────────────────────────────────────────────────────────────

const ERRORS: Record<string, string> = {
  forbidden_role: 'Ask the shop owner or an admin to do this.',
  has_payments: 'This invoice has payments. Issue a credit note for them first, then void it.',
  amount_exceeds_balance: 'This is more than the balance due.',
  invalid_paid_on: 'The date paid cannot be in the future or more than 60 days before the issue date.',
  not_draft: 'This document is no longer a draft. Reload to see its current state.',
  tax_invoice_locked: 'A tax invoice cannot be changed after issue. Issue a credit note and a new invoice.',
  revision_locked: 'This invoice has payments, a claim or a credit note and can no longer be changed.',
  card_unavailable: 'Card payments are not turned on for this shop.',
  tax_fields_required: 'A tax invoice needs a few more details before it can be issued.',
};

/** The banner copy for a failed action: the design's wording for known codes, else the server message. */
export function errorText(e: any, overrides: Record<string, string> = {}): string {
  const code = e?.code as string | undefined;
  if (code && overrides[code]) return overrides[code];
  if (code && ERRORS[code]) return ERRORS[code];
  return e?.message || 'Something went wrong. Try again.';
}

const FIELD_LABEL: Record<string, string> = {
  supplyDate: 'Supply date', shopTin: 'Shop TIN', 'seller.tin': 'Shop TIN', 'customer.tin': 'Customer TIN',
  'customer.name': 'Customer name', customer: 'Customer', lines: 'Lines', dueDate: 'Due date', issueDate: 'Issue date',
};
const FIELD_CODE: Record<string, string> = { required: 'is required', invalid: 'is not valid' };

/** `{ supplyDate: 'required' }` → ["Supply date is required"]. */
export function fieldProblems(fields: Record<string, string> | undefined): string[] {
  return Object.entries(fields || {}).map(([k, v]) => {
    const line = /^lines\[(\d+)\]\.(\w+)$/.exec(k);
    const label = line ? `Line ${Number(line[1]) + 1} ${line[2]}` : FIELD_LABEL[k] ?? k;
    return `${label} ${FIELD_CODE[v] ?? v.replace(/_/g, ' ')}`;
  });
}
